import { useCallback, useEffect, useMemo, useState } from "react";
import useYearsQuery from "@services/api/years/years.api";
import { YearProps } from "@services/api/years/years.types";
import useParameters from "../app/parameters/useParameter";

export default function useYears() {
  const { usePageQuery } = useYearsQuery();
  const { data: fetchedYears } = usePageQuery({
    variables: {
      sort: ["startDate,desc"],
      size: 9999
    }
  });

  const parameters = useParameters();
  const isVisible = useCallback(
    (externalId: string) => {
      if (!externalId) return false;
      const visibleYears = (parameters["visible.years"] as string[]) || [];
      return !visibleYears.length || visibleYears.includes(externalId);
    },
    [parameters]
  );

  const [years, setYears] = useState<YearProps[] | undefined>(
    fetchedYears?.content.filter((year) => isVisible(year.externalId))
  );

  useEffect(() => {
    if (years) return;
    if (!fetchedYears) return;
    const filteredYears = fetchedYears.content.filter((year) =>
      isVisible(year.externalId)
    );
    setYears(filteredYears);
  }, [fetchedYears, years, isVisible]);

  const currentYear = useMemo(() => {
    if (!years) return;
    return years.find(({ active }) => active) || years[0];
  }, [years]);

  const getYear = useCallback(
    (externalId?: string) => {
      if (!externalId) {
        return currentYear;
      }
      return years?.find((year) => year.externalId === externalId);
    },
    [currentYear, years]
  );

  return {
    years,
    currentYear: currentYear as YearProps,
    getYear,
    isVisible
  };
}
